// components/Employee/EmployeeForm.jsx
import React, { useState } from 'react';
import { UserIcon, CheckCircleIcon, PhotoIcon } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';

const employmentTypes = [
  { value: 0, label: 'Full Time' },
  { value: 1, label: 'Part Time' },
  { value: 2, label: 'Contract' },
  { value: 3, label: 'Intern' }
];

const emergencyContactRelations = [
  'Spouse', 'Parent', 'Sibling', 'Child', 'Friend', 'Other'
];

const EmployeeForm = ({ onSubmit, onCancel, initialData, isEditing = false }) => {
  const [formData, setFormData] = useState(initialData || {
    employeeCode: '', firstName: '', lastName: '', email: '', phone: '',
    profilePictureUrl: '', bio: '', position: '', dateOfBirth: '', dateOfJoining: '',
    workLocation: '', employmentType: 0, reportsToId: null, skillsJson: '[]',
    interestsJson: '[]', socialMediaLinksJson: '{}', emergencyContactName: '',
    emergencyContactPhone: '', emergencyContactRelation: '', companyId: '',
    branchId: '', departmentId: '', divisionId: '', isActive: true
  });

  const [profilePreview, setProfilePreview] = useState(initialData?.profilePictureUrl || '');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleProfileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, profilePictureUrl: file }));
      const reader = new FileReader();
      reader.onloadend = () => setProfilePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleTextAreaChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (formData.phone && !/^\+?[\d\s-()]+$/.test(formData.phone)) newErrors.phone = 'Phone number is invalid';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const cleanedData = {
        ...formData,
        companyId: formData.companyId ? parseInt(formData.companyId) : null,
        branchId: formData.branchId ? parseInt(formData.branchId) : null,
        departmentId: formData.departmentId ? parseInt(formData.departmentId) : null,
        divisionId: formData.divisionId ? parseInt(formData.divisionId) : null,
        reportsToId: formData.reportsToId ? parseInt(formData.reportsToId) : null,
        employmentType: formData.employmentType ? parseInt(formData.employmentType) : 0
      };
      await onSubmit(cleanedData);
    } catch (error) {
      setErrors({ general: error.response?.data?.message || 'Error saving employee' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 bg-gradient-to-br from-[var(--tint)] via-white to-[var(--tint)] p-6 rounded-2xl shadow-md">
      {/* Left Panel */}
      <div className="w-full lg:w-1/4 bg-gradient-to-br from-[var(--tint)] via-white to-[var(--tint)] rounded-2xl shadow-md p-6 border border-[var(--tint-line)] h-fit">
        <img
          src="/src/assets/employee/employeeleft.png"
          alt="Employee Settings"
          className="w-48 h-48 object-contain mb-3"
        />
        <h2 className="text-lg font-semibold text-gray-800">Employee Settings</h2>
        <p className="text-gray-500 text-sm mt-1 leading-snug">
          Configure employee details, upload profile photo, and manage information.
        </p>
      </div>

      {/* Right Form Panel */}
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex-1 bg-gradient-to-br from-[var(--tint)] via-white to-[var(--tint)] rounded-xl shadow-sm p-6 border border-[var(--tint-line)]"
      >
        {errors.general && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
            <p className="text-red-700 text-sm">{errors.general}</p>
          </div>
        )}

        {/* Profile Picture Upload */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative">
            {profilePreview ? (
              <img
                src={profilePreview}
                alt="Profile"
                className="w-32 h-32 object-cover rounded-full shadow-lg border-4 border-white"
              />
            ) : (
              <div className="w-32 h-32 flex flex-col items-center justify-center rounded-full bg-[var(--tint)] border-4 border-white text-[var(--accent)] shadow-lg">
                <UserIcon className="w-12 h-12 mb-2" />
                <span className="text-xs text-center">No Photo</span>
              </div>
            )}
          </div>
          <label className="mt-4 cursor-pointer bg-[var(--accent)] text-white px-4 py-2 rounded-lg hover:bg-[var(--accent-strong)] transition-colors text-sm font-medium">
            Upload Photo
            <input type="file" accept="image/*" className="hidden" onChange={handleProfileChange} />
          </label>
        </div>

        {/* Basic Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {[
            { label: 'Employee Code', name: 'employeeCode', type: 'text', placeholder: 'EMP001' },
            { label: 'First Name *', name: 'firstName', type: 'text', required: true, placeholder: 'John' },
            { label: 'Last Name *', name: 'lastName', type: 'text', required: true, placeholder: 'Doe' },
            { label: 'Email *', name: 'email', type: 'email', required: true, placeholder: 'john.doe@company.com' },
            { label: 'Phone', name: 'phone', type: 'tel', placeholder: '+1 (555) 000-0000' },
            { label: 'Position', name: 'position', type: 'text', placeholder: 'Software Engineer' },
          ].map((field) => (
            <div key={field.name}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {field.label}
              </label>
              <input
                type={field.type}
                name={field.name}
                value={formData[field.name] || ''}
                onChange={handleChange}
                required={field.required}
                placeholder={field.placeholder}
                className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 transition-colors ${
                  errors[field.name] ? 'border-red-500' : ''
                }`}
              />
              {errors[field.name] && (
                <p className="text-red-500 text-sm mt-1">{errors[field.name]}</p>
              )}
            </div>
          ))}

          {/* Employment Details */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Employment Type</label>
            <select
              name="employmentType"
              value={formData.employmentType}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm bg-white"
            >
              {employmentTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Work Location</label>
            <input
              type="text"
              name="workLocation"
              value={formData.workLocation || ''}
              onChange={handleChange}
              placeholder="New York Office"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
            <input
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth || ''}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date of Joining</label>
            <input
              type="date"
              name="dateOfJoining"
              value={formData.dateOfJoining || ''}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800"
            />
          </div>
        </div>

        {/* Emergency Contact Information */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Emergency Contact</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Name</label>
              <input
                type="text"
                name="emergencyContactName"
                value={formData.emergencyContactName || ''}
                onChange={handleChange}
                placeholder="Jane Doe"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone</label>
              <input
                type="tel"
                name="emergencyContactPhone"
                value={formData.emergencyContactPhone || ''}
                onChange={handleChange}
                placeholder="+1 (555) 000-0000"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
              <select
                name="emergencyContactRelation"
                value={formData.emergencyContactRelation || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm bg-white"
              >
                <option value="">Select Relationship</option>
                {emergencyContactRelations.map(relation => (
                  <option key={relation} value={relation}>{relation}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
            <textarea
              name="bio"
              value={formData.bio || ''}
              onChange={(e) => handleTextAreaChange('bio', e.target.value)}
              rows={4}
              placeholder="Brief bio about the employee..."
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Skills (comma separated)</label>
            <textarea
              name="skillsJson"
              value={formData.skillsJson || ''}
              onChange={(e) => handleTextAreaChange('skillsJson', e.target.value)}
              rows={4}
              placeholder="JavaScript, React, Node.js, Python"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 resize-none"
            />
          </div>
        </div>

        {/* Interests */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Interests (comma separated)</label>
          <textarea
            name="interestsJson"
            value={formData.interestsJson || ''}
            onChange={(e) => handleTextAreaChange('interestsJson', e.target.value)}
            rows={2}
            placeholder="Reading, Traveling, Photography, Sports"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 resize-none"
          />
        </div>

        {/* Active Status */}
        {isEditing && (
          <div className="flex items-center space-x-3 mb-6 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="w-5 h-5 text-[var(--accent)] rounded border-gray-300 focus:ring-[var(--accent)]"
            />
            <label className="text-sm font-medium text-gray-700">Active Employee</label>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 bg-[var(--accent)] text-white rounded-lg hover:bg-[var(--accent-strong)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4"></span>
                Saving...
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5" />
                {isEditing ? 'Update Employee' : 'Create Employee'}
              </>
            )}
          </button>
        </div>
      </motion.form>
    </div>
  );
};

export default EmployeeForm;